import { useState } from 'react';
import { Plus, Search, Download, Eye, ExternalLink, CalendarIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Link, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { NovaFaturaModal } from '@/components/faturas/NovaFaturaModal';
import { exportInvoicesToExcel } from '@/lib/exportExcel';
import { toast } from 'sonner';

interface Invoice {
  id: string;
  amount: number;
  due_date: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  paid_at: string | null;
  description: string | null;
  external_id: string | null;
  customer: {
    id: string;
    name: string;
    tax_id: string;
  };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value / 100);
}

const statusColors: Record<string, string> = {
  pending: 'bg-[hsl(var(--basic-100))] text-[hsl(var(--basic-700))]',
  paid: 'bg-[hsl(var(--accent-100))] text-[hsl(var(--accent-700))]',
  overdue: 'bg-[hsl(var(--careful-100))] text-[hsl(var(--careful-700))]',
  cancelled: 'bg-muted text-muted-foreground'
};

const statusLabels: Record<string, string> = {
  pending: 'Pendente',
  paid: 'Pago',
  overdue: 'Vencido',
  cancelled: 'Cancelado'
};

const Faturas = () => {
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [showModal, setShowModal] = useState(false);

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['all-invoices', statusFilter],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) return [];

      let query = supabase
        .from('invoices')
        .select(`
          *,
          customer:customers!inner(id, name, tax_id)
        `)
        .eq('company_id', profile.company_id)
        .order('due_date', { ascending: false });

      if (statusFilter !== 'all' && ['pending', 'paid', 'overdue', 'cancelled'].includes(statusFilter)) {
        query = query.eq('status', statusFilter as 'pending' | 'paid' | 'overdue' | 'cancelled');
      }

      const { data, error } = await query.limit(100);
      if (error) throw error;
      return data as unknown as Invoice[];
    }
  });

  // Filter by search and date range
  const filteredInvoices = invoices.filter(inv => {
    // Text search
    if (search) {
      const searchLower = search.toLowerCase();
      const matchesSearch = 
        inv.customer.name.toLowerCase().includes(searchLower) ||
        inv.customer.tax_id.includes(search) ||
        inv.description?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Date filter
    if (dateFrom || dateTo) {
      const dueDate = new Date(inv.due_date);
      if (dateFrom && dueDate < dateFrom) return false;
      if (dateTo) {
        const endOfDay = new Date(dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        if (dueDate > endOfDay) return false;
      }
    }

    return true;
  });

  // Calculate totals
  const totals = {
    all: invoices.reduce((sum, inv) => sum + inv.amount, 0),
    pending: invoices.filter(i => i.status === 'pending').reduce((sum, inv) => sum + inv.amount, 0),
    overdue: invoices.filter(i => i.status === 'overdue').reduce((sum, inv) => sum + inv.amount, 0),
    paid: invoices.filter(i => i.status === 'paid').reduce((sum, inv) => sum + inv.amount, 0)
  };

  const handleExportExcel = () => {
    if (filteredInvoices.length === 0) {
      toast.error('Nenhuma fatura para exportar');
      return;
    }
    exportInvoicesToExcel(filteredInvoices, 'faturas');
    toast.success('Arquivo Excel gerado com sucesso!');
  };

  const clearDateFilter = () => {
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const hasDateFilter = dateFrom || dateTo;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Contas a Receber</h1>
          <p className="text-muted-foreground">Gerencie todas as faturas e cobranças</p>
        </div>
        <Button className="gap-2 hover-pop" onClick={() => setShowModal(true)}>
          <Plus className="h-4 w-4" />
          Nova Fatura
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
          <p className="text-xl font-bold text-[hsl(var(--basic-900))]">{formatCurrency(totals.all)}</p>
          <p className="text-xs text-muted-foreground">{invoices.length} faturas</p>
        </div>
        <div className="bg-card rounded-xl border p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Pendente</p>
          <p className="text-xl font-bold text-[hsl(var(--basic-700))]">{formatCurrency(totals.pending)}</p>
          <p className="text-xs text-muted-foreground">{invoices.filter(i => i.status === 'pending').length} faturas</p>
        </div>
        <div className="bg-[hsl(var(--careful-50))] rounded-xl border border-[hsl(var(--careful-200))] p-4">
          <p className="text-xs text-[hsl(var(--careful-600))] uppercase tracking-wider">Vencido</p>
          <p className="text-xl font-bold text-[hsl(var(--careful-600))]">{formatCurrency(totals.overdue)}</p>
          <p className="text-xs text-[hsl(var(--careful-600))]">{invoices.filter(i => i.status === 'overdue').length} faturas</p>
        </div>
        <div className="bg-[hsl(var(--accent-50))] rounded-xl border border-[hsl(var(--accent-200))] p-4">
          <p className="text-xs text-[hsl(var(--accent-600))] uppercase tracking-wider">Recebido</p>
          <p className="text-xl font-bold text-[hsl(var(--accent-600))]">{formatCurrency(totals.paid)}</p>
          <p className="text-xs text-[hsl(var(--accent-600))]">{invoices.filter(i => i.status === 'paid').length} faturas</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList>
            <TabsTrigger value="all">Todas</TabsTrigger>
            <TabsTrigger value="pending">Pendentes</TabsTrigger>
            <TabsTrigger value="overdue">Vencidas</TabsTrigger>
            <TabsTrigger value="paid">Pagas</TabsTrigger>
            <TabsTrigger value="cancelled">Canceladas</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex gap-2 flex-wrap">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Pesquisar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 w-48"
            />
          </div>

          {/* Date Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className={cn(
                  "gap-2",
                  hasDateFilter && "border-primary text-primary"
                )}
              >
                <CalendarIcon className="h-4 w-4" />
                {hasDateFilter ? (
                  <span className="text-sm">
                    {dateFrom ? format(dateFrom, 'dd/MM', { locale: ptBR }) : '...'} 
                    {' - '} 
                    {dateTo ? format(dateTo, 'dd/MM', { locale: ptBR }) : '...'}
                  </span>
                ) : (
                  'Vencimento'
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-4" align="end">
              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium">De</p>
                  <Calendar
                    mode="single"
                    selected={dateFrom}
                    onSelect={setDateFrom}
                    locale={ptBR}
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Até</p>
                  <Calendar
                    mode="single"
                    selected={dateTo}
                    onSelect={setDateTo}
                    locale={ptBR}
                  />
                </div>
                {hasDateFilter && (
                  <Button variant="ghost" size="sm" onClick={clearDateFilter} className="w-full">
                    <X className="h-4 w-4 mr-2" />
                    Limpar filtro
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Export */}
          <Button variant="outline" className="gap-2" onClick={handleExportExcel}>
            <Download className="h-4 w-4" />
            XLS
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border overflow-hidden">
        <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_8rem_6rem] gap-4 p-4 bg-muted/50 border-b font-medium text-sm text-muted-foreground">
          <span>Valor</span>
          <span>Cliente</span>
          <span>Vencimento</span>
          <span>Pago em</span>
          <span>Status</span>
          <span className="text-right">Ações</span>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Carregando...</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground italic">
            {search || hasDateFilter ? 'Nenhuma fatura encontrada.' : 'Nenhuma fatura cadastrada.'}
          </div>
        ) : (
          <div className="divide-y">
            {filteredInvoices.map((invoice) => (
              <div 
                key={invoice.id} 
                className="grid grid-cols-[1fr_1.5fr_1fr_1fr_8rem_6rem] gap-4 p-4 hover:bg-muted/30 transition-colors items-center"
              >
                <span className="font-bold text-[hsl(var(--basic-900))]">
                  {formatCurrency(invoice.amount)}
                </span>
                <div className="min-w-0">
                  <Link 
                    to={`/clientes/${invoice.customer.id}`}
                    state={{ from: location.pathname }}
                    className="font-medium text-[hsl(var(--basic-900))] hover:underline truncate block"
                  >
                    {invoice.customer.name}
                  </Link>
                  <p className="text-xs text-muted-foreground font-mono truncate">
                    {invoice.customer.tax_id}
                  </p>
                </div>
                <span className="text-sm text-[hsl(var(--basic-700))]">
                  {new Date(invoice.due_date).toLocaleDateString('pt-BR')}
                </span>
                <span className="text-sm text-muted-foreground">
                  {invoice.paid_at 
                    ? new Date(invoice.paid_at).toLocaleDateString('pt-BR')
                    : '-'
                  }
                </span>
                <span className={`px-2 py-1 rounded text-xs font-bold uppercase text-center ${statusColors[invoice.status]}`}>
                  {statusLabels[invoice.status]}
                </span>
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                    <Link to={`/checkout/${invoice.id}`} target="_blank">
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                    <Link to={`/faturas/${invoice.id}`} state={{ from: location.pathname }}>
                      <Eye className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <NovaFaturaModal open={showModal} onOpenChange={setShowModal} />
    </div>
  );
};

export default Faturas;
